import type { AccessLevel } from "@woven/contracts";
import { z } from "zod";

export const DEFAULT_CONTROL_API_URL = "http://localhost:8787";

const apiBaseSchema = z.union([
  z.literal(""),
  z.literal("/"),
  z
    .string()
    .url()
    .refine((value) => {
      if (!URL.canParse(value)) return false;
      const url = new URL(value);
      return (
        ["http:", "https:"].includes(url.protocol) &&
        !url.username &&
        !url.password &&
        !url.search &&
        !url.hash
      );
    }),
]);

export function controlApiBase(input: Record<string, unknown>): string {
  return apiBaseSchema
    .parse(input.VITE_CONTROL_API_URL ?? (input.PROD === true ? "" : DEFAULT_CONTROL_API_URL))
    .replace(/\/+$/, "");
}

const webEnvSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().optional(),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().optional(),
  VITE_FIREBASE_PROJECT_ID: z.string().optional(),
  VITE_FIREBASE_APP_ID: z.string().optional(),
  VITE_FIREBASE_APPCHECK_SITE_KEY: z.string().optional(),
  VITE_CONTROL_API_URL: apiBaseSchema.optional(),
  VITE_USE_FIREBASE_EMULATORS: z.enum(["true", "false"]).optional(),
  VITE_CONTROL_API_TOKEN: z.string().optional(),
  VITE_CONTROL_API_APPCHECK_TOKEN: z.string().optional(),
  VITE_DEV_ACCESS_LEVEL: z.enum(["lite", "pro", "admin"]).optional(),
  VITE_LOCAL_ADMIN_BYPASS: z.enum(["true", "false"]).optional(),
});

/**
 * Validated browser configuration. Firebase browser config is public, but the
 * code never hardcodes environment-specific values — they are read from Vite
 * env vars.
 */
export interface WebEnv {
  firebaseApiKey: string;
  firebaseAuthDomain: string;
  firebaseProjectId: string;
  firebaseAppId: string;
  appCheckSiteKey: string;
  controlApiUrl: string;
  useEmulators: boolean;
  controlApiToken: string;
  controlApiAppCheckToken: string;
  devAccessLevel: AccessLevel | null;
  localAdminBypass: boolean;
}

const envCache = new WeakMap<object, WebEnv | null>();

/** Parses/validates the raw env object (usually `import.meta.env`). */
export function parseWebEnv(input: Record<string, unknown>): WebEnv | null {
  const cached = envCache.get(input);
  if (cached !== undefined) {
    return cached;
  }

  const parsed = webEnvSchema.safeParse(input);
  let result: WebEnv | null = null;
  if (parsed.success) {
    const env = parsed.data;
    const useEmulators = env.VITE_USE_FIREBASE_EMULATORS === "true";

    const hasProject = Boolean(env.VITE_FIREBASE_PROJECT_ID);
    const hasFullConfig = Boolean(
      env.VITE_FIREBASE_API_KEY &&
      env.VITE_FIREBASE_AUTH_DOMAIN &&
      env.VITE_FIREBASE_PROJECT_ID &&
      env.VITE_FIREBASE_APP_ID
    );

    // Emulators only need a project id; a live app needs the full public config.
    if ((useEmulators && hasProject) || (!useEmulators && hasFullConfig)) {
      result = {
        firebaseApiKey: env.VITE_FIREBASE_API_KEY ?? "",
        firebaseAuthDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
        firebaseProjectId: env.VITE_FIREBASE_PROJECT_ID ?? "",
        firebaseAppId: env.VITE_FIREBASE_APP_ID ?? "",
        appCheckSiteKey: env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? "",
        controlApiUrl: controlApiBase(input),
        useEmulators,
        controlApiToken: env.VITE_CONTROL_API_TOKEN ?? "",
        controlApiAppCheckToken: env.VITE_CONTROL_API_APPCHECK_TOKEN ?? "",
        devAccessLevel: env.VITE_DEV_ACCESS_LEVEL ?? null,
        localAdminBypass: env.VITE_LOCAL_ADMIN_BYPASS === "true",
      };
    }
  }

  envCache.set(input, result);
  return result;
}

const ambientWebEnv = parseWebEnv(import.meta.env);

/** Returns the ambient browser env, or null when Firebase is not configured. */
export function getWebEnv(): WebEnv | null {
  return ambientWebEnv;
}
